import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './FeedbackModal.css';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type FeedbackType = 'bug' | 'feature' | 'balance' | 'other';

// Extend Window interface for Turnstile
declare global {
    interface Window {
        turnstile?: {
            render: (element: string | HTMLElement, options: any) => string;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
        };
        onTurnstileLoad?: () => void;
    }
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [feedbackType, setFeedbackType] = useState<FeedbackType>('other');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [turnstileToken, setTurnstileToken] = useState<string>('');
    const [turnstileLoaded, setTurnstileLoaded] = useState(false);
    const turnstileWidgetId = useRef<string | null>(null);
    const turnstileContainerRef = useRef<HTMLDivElement>(null);

    // Get Turnstile site key from environment variable
    const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

    // Load Turnstile script
    useEffect(() => {
        console.log('[Feedback] Turnstile Site Key configured:', !!TURNSTILE_SITE_KEY);

        if (!TURNSTILE_SITE_KEY) {
            console.log('[Feedback] Turnstile not configured, skipping captcha');
            setTurnstileLoaded(true); // Skip if not configured
            return;
        }

        // Check if script already loaded
        if (window.turnstile) {
            console.log('[Feedback] Turnstile script already loaded');
            setTurnstileLoaded(true);
            return;
        }

        console.log('[Feedback] Loading Turnstile script');

        // Add script to page
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.defer = true;

        window.onTurnstileLoad = () => {
            console.log('[Feedback] Turnstile script loaded');
            setTurnstileLoaded(true);
        };

        script.onload = () => {
            console.log('[Feedback] Script onload fired');
            if (window.onTurnstileLoad) {
                window.onTurnstileLoad();
            }
        };

        script.onerror = () => {
            console.error('[Feedback] Failed to load Turnstile script');
        };

        document.head.appendChild(script);

        return () => {
            // Cleanup
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [TURNSTILE_SITE_KEY]);

    // Render Turnstile widget when modal opens and script is loaded
    useEffect(() => {
        console.log('[Feedback] Widget render check - isOpen:', isOpen, 'loaded:', turnstileLoaded, 'window.turnstile:', !!window.turnstile, 'sitekey:', !!TURNSTILE_SITE_KEY);

        if (!isOpen || !turnstileLoaded || !window.turnstile || !TURNSTILE_SITE_KEY) {
            return;
        }

        console.log('[Feedback] Rendering Turnstile widget');

        // Wait for container to be in DOM
        setTimeout(() => {
            if (turnstileContainerRef.current && !turnstileWidgetId.current) {
                console.log('[Feedback] Container found, calling turnstile.render()');
                try {
                    turnstileWidgetId.current = window.turnstile!.render(turnstileContainerRef.current, {
                        sitekey: TURNSTILE_SITE_KEY,
                        callback: (token: string) => {
                            console.log('[Feedback] Turnstile callback - token received:', !!token);
                            setTurnstileToken(token);
                        },
                        'error-callback': () => {
                            console.warn('[Feedback] Turnstile error callback');
                            setTurnstileToken('');
                        },
                        'expired-callback': () => {
                            console.warn('[Feedback] Turnstile expired callback');
                            setTurnstileToken('');
                        },
                        theme: 'light',
                        size: 'normal',
                    });
                    console.log('[Feedback] Widget rendered, ID:', turnstileWidgetId.current);
                } catch (error) {
                    console.error('[Feedback] Error rendering Turnstile widget:', error);
                }
            } else {
                console.log('[Feedback] Container not found or widget already rendered');
            }
        }, 100);

        return () => {
            // Cleanup widget when modal closes
            if (turnstileWidgetId.current && window.turnstile) {
                try {
                    window.turnstile.remove(turnstileWidgetId.current);
                } catch (e) {
                    // Ignore errors during cleanup
                }
                turnstileWidgetId.current = null;
            }
        };
    }, [isOpen, turnstileLoaded, TURNSTILE_SITE_KEY]);

    const feedbackTypeOptions = [
        { value: 'bug' as const, label: '🐛 Bug Report', description: 'Report a technical issue' },
        { value: 'feature' as const, label: '✨ Feature Request', description: 'Suggest a new feature' },
        { value: 'balance' as const, label: '⚖️ Balance Issue', description: 'Game balance feedback' },
        { value: 'other' as const, label: '📝 General Feedback', description: 'Other comments' },
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('[Feedback] Form submitted');
        console.log('[Feedback] Turnstile enabled:', !!TURNSTILE_SITE_KEY);
        console.log('[Feedback] Token status:', TURNSTILE_SITE_KEY ? (turnstileToken ? 'present' : 'MISSING') : 'N/A');

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Pages Function will be available at /api/feedback
            const apiUrl = '/api/feedback';

            const payload = {
                name,
                email,
                feedbackType,
                message,
                ...(TURNSTILE_SITE_KEY && { turnstileToken }), // Include token if Turnstile is enabled
            };

            console.log('[Feedback] Sending payload:', {
                name,
                email,
                feedbackType,
                messageLength: message.length,
                hasTurnstileToken: !!turnstileToken,
            });

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            console.log('[Feedback] Response status:', response.status);

            const data = await response.json();

            console.log('[Feedback] Response data:', data);

            if (data.success) {
                setSubmitStatus({
                    type: 'success',
                    message: data.message || 'Thank you for your feedback!',
                });
                // Clear form
                setName('');
                setEmail('');
                setFeedbackType('other');
                setMessage('');
                setTurnstileToken('');
                
                // Reset Turnstile widget
                if (turnstileWidgetId.current && window.turnstile) {
                    window.turnstile.reset(turnstileWidgetId.current);
                }
                
                // Close modal after a short delay
                setTimeout(onClose, 2000);
            } else {
                console.error('[Feedback] Server returned error:', data.message);
                setSubmitStatus({
                    type: 'error',
                    message: data.message || 'Failed to submit feedback. Please try again.',
                });

                // Reset Turnstile widget on error
                if (turnstileWidgetId.current && window.turnstile) {
                    window.turnstile.reset(turnstileWidgetId.current);
                    setTurnstileToken('');
                }
            }
        } catch (error: any) {
            console.error('[Feedback] Exception during submission:', error);
            setSubmitStatus({
                type: 'error',
                message: error.message || 'Failed to submit feedback. Please try again later.',
            });

            // Reset Turnstile widget on error
            if (turnstileWidgetId.current && window.turnstile) {
                window.turnstile.reset(turnstileWidgetId.current);
                setTurnstileToken('');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="feedback-modal-wrapper">
            <div className="feedback-modal">
                <div className="feedback-header">
                    <h2>Send Us Your Feedback</h2>
                    <p className="feedback-subtitle">
                        Help us improve Pixel Soccer Manager. Your feedback is valuable!
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="feedback-form">
                    {/* Feedback Type Selection */}
                    <div className="feedback-section">
                        <label className="feedback-label">Feedback Type</label>
                        <div className="feedback-type-grid">
                            {feedbackTypeOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`feedback-type-button ${feedbackType === option.value ? 'active' : ''}`}
                                    onClick={() => setFeedbackType(option.value)}
                                    title={option.description}
                                >
                                    <span className="type-label">{option.label}</span>
                                    <span className="type-description">{option.description}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Name Field */}
                    <div className="feedback-section">
                        <label htmlFor="feedback-name" className="feedback-label">
                            Your Name
                        </label>
                        <input
                            id="feedback-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter your name"
                            required
                            className="feedback-input"
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Email Field */}
                    <div className="feedback-section">
                        <label htmlFor="feedback-email" className="feedback-label">
                            Your Email
                        </label>
                        <input
                            id="feedback-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your-email@example.com"
                            required
                            className="feedback-input"
                            disabled={isSubmitting}
                        />
                        <p className="feedback-hint">So we can reply if needed</p>
                    </div>

                    {/* Message Field */}
                    <div className="feedback-section">
                        <label htmlFor="feedback-message" className="feedback-label">
                            Message
                        </label>
                        <textarea
                            id="feedback-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Tell us what you think... (minimum 10 characters)"
                            required
                            minLength={10}
                            maxLength={5000}
                            rows={6}
                            className="feedback-textarea"
                            disabled={isSubmitting}
                        />
                        <div className="feedback-counter">
                            {message.length} / 5000
                        </div>
                    </div>

                    {/* Turnstile CAPTCHA */}
                    {TURNSTILE_SITE_KEY && (
                        <div className="feedback-section">
                            <div 
                                ref={turnstileContainerRef}
                                className="turnstile-container"
                                style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}
                            />
                        </div>
                    )}

                    {/* Status Messages */}
                    {submitStatus && (
                        <div className={`feedback-status ${submitStatus.type}`}>
                            {submitStatus.type === 'success' && '✓ '}
                            {submitStatus.type === 'error' && '✕ '}
                            {submitStatus.message}
                        </div>
                    )}

                    {/* Submit Button */}
                    <div className="feedback-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="button secondary"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="button primary"
                            disabled={
                                isSubmitting || 
                                !name || 
                                !email || 
                                !message || 
                                message.length < 10 ||
                                (TURNSTILE_SITE_KEY && !turnstileToken) // Require token if Turnstile enabled
                            }
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '6px' }} />
                                    Send Feedback
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};